import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Link2, Plus, RotateCcw } from 'lucide-react';
import { adviserApi, type ClientSummary, type PracticeMember } from '../../advisers/api';
import { NOTIFICATIONS_CHANGED_EVENT } from '../../advisers/enquiriesApi';
import { companyOnboardingApi } from '../../company-context/api';

const billingLabels: Record<ClientSummary['billingStatus'], string> = {
  demo: 'No paid billing',
  not_started: 'Billing not started',
  active: 'Billing active',
  trialing: 'Billing trial',
  attention: 'Billing needs attention',
  unknown: 'Billing unavailable',
};
const hasProgressSummary = (client: ClientSummary) =>
  !client.summaryErrors?.progress &&
  typeof client.tasksCompleted === 'number' &&
  typeof client.totalTasks === 'number' &&
  typeof client.objectivesCount === 'number';
const hasClientAccess = (client: ClientSummary) =>
  client.status === 'active' ||
  client.status === 'approved' ||
  client.status === 'ownership_pending' ||
  client.isDemo;

export default function AdviserClientsPanel({
  practiceId,
  clients,
  members,
  canManage,
  onChanged,
}: {
  practiceId: string;
  clients: ClientSummary[];
  members: PracticeMember[];
  canManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [mode, setMode] = useState<'create' | 'link' | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [confirm, setConfirm] = useState<{
    kind: 'reset' | 'disconnect';
    client: ClientSummary;
  } | null>(null);
  const [assignment, setAssignment] = useState<ClientSummary | null>(null);
  const [ownershipInvite, setOwnershipInvite] = useState<ClientSummary | null>(null);
  const [userIds, setUserIds] = useState<string[]>([]);
  const perform = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      await onChanged();
      setNotice(message);
      setConfirm(null);
      setAssignment(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update client.');
    } finally {
      setBusy(false);
    }
  };
  const filtered = clients.filter(
    (client) =>
      (filter === 'all' || (filter === 'demo' ? client.isDemo : !client.isDemo)) &&
      [client.name, client.industry].join(' ').toLowerCase().includes(search.toLowerCase())
  );
  const summaryUnavailable = clients.some(
    (client) =>
      hasClientAccess(client) &&
      (!hasProgressSummary(client) ||
        (!client.isDemo &&
          (Boolean(client.summaryErrors?.billing) ||
            !client.billingStatus ||
            client.billingStatus === 'unknown')))
  );
  return (
    <section>
      <div className="adviser-section-heading">
        <div>
          <h2>Your client companies</h2>
          <p>
            {canManage
              ? 'Keep each client’s strategy, people and delivery together.'
              : 'Open the clients assigned to you by your practice owner.'}
          </p>
        </div>
        {canManage && (
          <div className="adviser-actions">
            <button
              type="button"
              className="adviser-button adviser-button-secondary"
              onClick={() => {
                setMode('link');
                setError('');
              }}
            >
              <Link2 size={16} />
              Link existing company
            </button>
            <button
              type="button"
              className="adviser-button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  const capabilities = await companyOnboardingApi.capabilities();
                  if (capabilities.enabled)
                    navigate(`/company/setup?practice=${encodeURIComponent(practiceId)}`);
                  else setMode('create');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Unable to open company setup.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Plus size={16} />
              Create client
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="adviser-success" role="status">
          {notice}
        </p>
      )}
      {inviteUrl && (
        <div className="adviser-notice">
          <strong>Client ownership invitation</strong>
          <p>
            Share this private link with the client owner. They retain their own company access
            after accepting.
          </p>
          <input
            aria-label="Client ownership invitation link"
            readOnly
            value={inviteUrl}
            onFocus={(event) => event.target.select()}
          />
        </div>
      )}
      <div className="adviser-client-tools">
        <label>
          Find a company
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or industry"
          />
        </label>
        <label>
          Show
          <select value={filter} onChange={(event) => setFilter(event.target.value)}>
            <option value="all">All companies</option>
            <option value="client">Client companies</option>
            <option value="demo">Demo companies</option>
          </select>
        </label>
        <span className="adviser-muted">
          {clients.filter((client) => !client.isDemo).length} clients ·{' '}
          {clients.filter((client) => client.isDemo).length} demos
        </span>
      </div>
      {canManage && (
        <div className="adviser-demo-bar">
          <div>
            <strong>Your reusable demo pack</strong>
            <p>
              Childcare, Restaurant and Landscaping. Private editable examples, with no paid
              subscriptions.
            </p>
          </div>
          <button
            className="adviser-button adviser-button-secondary"
            type="button"
            disabled={busy}
            onClick={() =>
              void perform(
                () => adviserApi.provisionDemos(practiceId),
                'Demo pack ready. Existing demos were preserved.'
              )
            }
          >
            Set up demo pack
          </button>
        </div>
      )}
      {summaryUnavailable && (
        <div className="adviser-summary-notice" role="status">
          <p>Some client progress or billing summaries are unavailable.</p>
          <button
            type="button"
            className="adviser-button adviser-button-secondary"
            disabled={refreshing || busy}
            onClick={async () => {
              setRefreshing(true);
              setError('');
              try {
                await onChanged();
              } catch (err) {
                setError(
                  err instanceof Error ? err.message : 'Unable to refresh client summaries.'
                );
              } finally {
                setRefreshing(false);
              }
            }}
          >
            {refreshing ? 'Refreshing…' : 'Retry summaries'}
          </button>
        </div>
      )}
      {filtered.length ? (
        <div className="adviser-client-list">
          {filtered.map((client) => {
            const progress = hasProgressSummary(client)
              ? client.totalTasks
                ? Math.round((client.tasksCompleted! / client.totalTasks) * 100)
                : 0
              : null;
            const billingStatus = client.isDemo
              ? 'demo'
              : client.summaryErrors?.billing
                ? 'unknown'
                : client.billingStatus || 'unknown';
            const canOpen = hasClientAccess(client);
            return (
              <article className="adviser-client-row" key={client.companyId}>
                <div className="adviser-client-identity">
                  <div className="adviser-company-icon">
                    <Building2 size={21} />
                  </div>
                  <div>
                    <h3>
                      {canOpen ? (
                        <Link to={`/company/${client.companyId}`}>{client.name}</Link>
                      ) : (
                        client.name
                      )}
                    </h3>
                    <p>
                      {client.industry || 'Industry not set'}{' '}
                      {client.isDemo && <span className="adviser-badge">Demo</span>}
                    </p>
                  </div>
                </div>
                <div className="adviser-client-progress">
                  {progress === null ? (
                    <span>
                      {client.status === 'pending'
                        ? 'Summary available after client approval'
                        : 'Progress summary unavailable'}
                    </span>
                  ) : (
                    <>
                      <span>
                        {client.objectivesCount} objectives · {client.tasksCompleted}/
                        {client.totalTasks} tasks complete
                      </span>
                      <progress
                        value={progress}
                        max={100}
                        aria-label={`${client.name} execution progress`}
                      />
                    </>
                  )}
                </div>
                <div>
                  <div className="adviser-client-statuses">
                    <span className={`adviser-status adviser-status-${client.status}`}>
                      {client.status === 'pending'
                        ? 'Awaiting client approval'
                        : client.ownershipStatus === 'pending'
                          ? 'Ownership handoff pending'
                          : client.isDemo
                            ? 'Demo access'
                            : 'Client-owned'}
                    </span>
                    <span className={`adviser-status adviser-status-${billingStatus}`}>
                      {client.status === 'pending'
                        ? 'Billing available after approval'
                        : billingLabels[billingStatus]}
                    </span>
                  </div>
                  {!client.isDemo && client.personalPlanningCoverage && (
                    <p className="adviser-muted">Planning covered by personal subscription.</p>
                  )}
                  <p className="adviser-muted">
                    {client.nextAction ||
                      (client.ownershipStatus === 'pending'
                        ? 'Invite the client owner to accept.'
                        : client.status === 'pending'
                          ? 'The company owner must approve access.'
                          : progress === null
                            ? 'Retry the client progress summary.'
                            : client.totalTasks === 0
                              ? 'Connect the plan to its first project.'
                              : progress === 100
                                ? 'Review completed work and the next priorities.'
                                : 'Review progress with your client.')}
                  </p>
                </div>
                <div className="adviser-client-actions">
                  {canOpen && (
                    <Link
                      className="adviser-button adviser-button-secondary"
                      to={`/company/${client.companyId}`}
                    >
                      Open <ArrowRight size={15} />
                    </Link>
                  )}
                  {canOpen && (
                    <Link
                      className="adviser-button adviser-button-quiet"
                      to={`/company/${client.companyId}/ai-plan`}
                    >
                      AI strategic plan <ArrowRight size={15} />
                    </Link>
                  )}
                  {canManage && (
                    <>
                      <button
                        type="button"
                        className="adviser-button adviser-button-quiet"
                        onClick={() => {
                          setAssignment(client);
                          setUserIds(client.assignedUserIds || []);
                        }}
                      >
                        Assign team
                      </button>
                      {client.ownershipStatus === 'pending' && (
                        <button
                          type="button"
                          className="adviser-button adviser-button-quiet"
                          onClick={() => setOwnershipInvite(client)}
                        >
                          Ownership invitation
                        </button>
                      )}
                      {client.isDemo ? (
                        <button
                          type="button"
                          className="adviser-button adviser-button-quiet"
                          onClick={() => setConfirm({ kind: 'reset', client })}
                        >
                          <RotateCcw size={14} />
                          Reset demo
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="adviser-button adviser-button-quiet"
                          onClick={() => setConfirm({ kind: 'disconnect', client })}
                        >
                          Disconnect
                        </button>
                      )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="adviser-empty">
          <h3>
            {clients.length
              ? 'No companies match your search.'
              : canManage
                ? 'Your first client starts here.'
                : 'No clients assigned yet.'}
          </h3>
          <p>
            {canManage
              ? 'Create a workspace, link an existing company or set up your demos.'
              : 'Your practice owner can assign client companies to you.'}
          </p>
        </div>
      )}
      {mode && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="adviser-client-dialog-title"
          >
            <h2 id="adviser-client-dialog-title">
              {mode === 'create' ? 'Create a client workspace' : 'Link an existing company'}
            </h2>
            <p>
              {mode === 'create'
                ? 'Prepare a draft with restricted adviser access. The client accepts an invitation to take direct ownership.'
                : 'Request collaboration with a company. Its owner must approve before your practice can open it.'}
            </p>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                setBusy(true);
                setError('');
                try {
                  if (mode === 'create') {
                    const result = await adviserApi.createClient(practiceId, {
                      name: String(form.get('name')),
                      size: String(form.get('size')),
                      industry: String(form.get('industry')),
                      description: String(form.get('description')),
                      ownerEmail: String(form.get('ownerEmail')),
                    });
                    setInviteUrl(result.acceptUrl || '');
                    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
                  } else {
                    await adviserApi.linkClient(practiceId, String(form.get('companyId')).trim());
                    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
                  }
                  await onChanged();
                  setMode(null);
                  setNotice(
                    mode === 'create'
                      ? 'Client draft created. Share the ownership invitation.'
                      : 'Link requested. The company owner can approve it from their adviser access screen.'
                  );
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Unable to save client.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <fieldset disabled={busy}>
                {mode === 'create' ? (
                  <div className="adviser-form-grid">
                    <label className="adviser-form-full">
                      Company name
                      <input name="name" required maxLength={200} />
                    </label>
                    <label>
                      Industry
                      <input name="industry" required maxLength={200} />
                    </label>
                    <label>
                      Company size
                      <select name="size">
                        <option value="small">Small (1–50)</option>
                        <option value="medium">Medium (51–200)</option>
                        <option value="large">Large (201+)</option>
                      </select>
                    </label>
                    <label className="adviser-form-full">
                      Company context
                      <textarea name="description" rows={3} required />
                    </label>
                    <label className="adviser-form-full">
                      Client owner's email
                      <input name="ownerEmail" type="email" required />
                    </label>
                  </div>
                ) : (
                  <label>
                    Company ID
                    <input
                      name="companyId"
                      required
                      placeholder="Ask the owner for the company ID from their workspace URL"
                    />
                  </label>
                )}
              </fieldset>
              {error && (
                <p className="adviser-error" role="alert">
                  {error}
                </p>
              )}
              <div className="adviser-actions">
                <button className="adviser-button" type="submit" disabled={busy}>
                  {busy
                    ? 'Saving…'
                    : mode === 'create'
                      ? 'Create and invite owner'
                      : 'Request client approval'}
                </button>
                <button
                  type="button"
                  className="adviser-button adviser-button-secondary"
                  disabled={busy}
                  onClick={() => setMode(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {confirm && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-reset-title"
          >
            <h2 id="demo-reset-title">
              {confirm.kind === 'reset'
                ? `Reset ${confirm.client.name}?`
                : `Disconnect ${confirm.client.name}?`}
            </h2>
            <p>
              {confirm.kind === 'reset'
                ? 'This replaces your practice’s demo changes with the standard template. It does not change another practice’s copy.'
                : 'Your practice will lose delegated access. The client keeps their company and data. Billing commitments remain separately managed.'}
            </p>
            {error && (
              <p className="adviser-error" role="alert">
                {error}
              </p>
            )}
            <div className="adviser-actions">
              <button
                type="button"
                className="adviser-button"
                disabled={busy}
                onClick={() =>
                  void perform(
                    () =>
                      confirm.kind === 'reset'
                        ? adviserApi.resetDemo(practiceId, confirm.client.companyId)
                        : adviserApi.disconnect(practiceId, confirm.client.companyId),
                    confirm.kind === 'reset'
                      ? 'Demo reset to its standard template.'
                      : 'Practice disconnected from company.'
                  )
                }
              >
                {busy
                  ? 'Working…'
                  : confirm.kind === 'reset'
                    ? 'Reset this demo'
                    : 'Disconnect practice'}
              </button>
              <button
                type="button"
                disabled={busy}
                className="adviser-button adviser-button-secondary"
                onClick={() => setConfirm(null)}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
      {ownershipInvite && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ownership-invite-title"
          >
            <h2 id="ownership-invite-title">Invite the owner of {ownershipInvite.name}</h2>
            <p>
              Create a fresh seven-day ownership invitation. The previous invitation becomes
              invalid.
            </p>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                const email = String(new FormData(event.currentTarget).get('ownerEmail'));
                setBusy(true);
                setError('');
                try {
                  const result = await adviserApi.reissueOwnership(
                    practiceId,
                    ownershipInvite.companyId,
                    email
                  );
                  setInviteUrl(result.acceptUrl);
                  setOwnershipInvite(null);
                  setNotice('New ownership invitation ready to share.');
                  await onChanged();
                } catch (err) {
                  setError(
                    err instanceof Error ? err.message : 'Unable to create ownership invitation.'
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label>
                Client owner email
                <input
                  name="ownerEmail"
                  type="email"
                  required
                  defaultValue={ownershipInvite.ownerEmail || ''}
                />
              </label>
              {error && (
                <p className="adviser-error" role="alert">
                  {error}
                </p>
              )}
              <div className="adviser-actions">
                <button className="adviser-button" type="submit" disabled={busy}>
                  Create ownership invitation
                </button>
                <button
                  type="button"
                  className="adviser-button adviser-button-secondary"
                  disabled={busy}
                  onClick={() => setOwnershipInvite(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {assignment && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assignment-title"
          >
            <h2 id="assignment-title">Assign staff to {assignment.name}</h2>
            <p>
              Practice owners have access to the whole roster. Staff only see clients selected here.
            </p>
            <div className="adviser-assignment-list">
              {members
                .filter((member) => member.role === 'staff')
                .map((member) => (
                  <label key={member.userId}>
                    <input
                      type="checkbox"
                      checked={userIds.includes(member.userId)}
                      onChange={(event) =>
                        setUserIds((current) =>
                          event.target.checked
                            ? [...current, member.userId]
                            : current.filter((id) => id !== member.userId)
                        )
                      }
                    />
                    {member.name || member.email}
                    <small>{member.email}</small>
                  </label>
                ))}
            </div>
            {members.every((member) => member.role !== 'staff') && (
              <p>Invite staff from the Team tab first.</p>
            )}
            {error && (
              <p className="adviser-error" role="alert">
                {error}
              </p>
            )}
            <div className="adviser-actions">
              <button
                type="button"
                className="adviser-button"
                disabled={busy}
                onClick={() =>
                  void perform(
                    () => adviserApi.assign(practiceId, assignment.companyId, userIds),
                    'Client assignments updated.'
                  )
                }
              >
                Save assignments
              </button>
              <button
                type="button"
                className="adviser-button adviser-button-secondary"
                disabled={busy}
                onClick={() => setAssignment(null)}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
